import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect, LocationPicker } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import ControlledImageSelector from '../../../../components/input/form/ControlledImageSelector';
import { LatLng } from 'leaflet';
import { Typography } from '@mui/material';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import { ISiteFormValues } from '../../../../@types/model/masterData/site/siteFormValues';
import SiteModelHelper from '../../../../@types/model/masterData/site/siteModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import { IOrganization } from '../../../../@types/model/masterData/organization/organization';
import { IContactInfo } from '../../../../@types/model/masterData/contactInfo/contactInfo';
import { IFtpDetail } from '../../../../@types/model/masterData/ftpDetail/ftpDetail';
import { IInspectionPoint } from '../../../../@types/model/masterData/inspectionPoint/inspectionPoint';
import OptionType from '../../../../@types/model/optionType';

interface ISiteFormProps {
    isLoading : boolean;
    initialValues : ISiteFormValues;
    onSubmit : (values : ISiteFormValues) => void;
    onClose : () => void;

    organizations : Array<IOrganization>;
    contactInfos : Array<IContactInfo>;
    ftpDetails : Array<IFtpDetail>;
    inspectionPoints : Array<IInspectionPoint>;
}

const SiteForm = (props : ISiteFormProps) : React.ReactElement => {
    const defaultCenterPosition = new LatLng(-23.5792, 30.1424);

    const { handleSubmit, control, setValue, formState: { isDirty, isValid }, watch, trigger } = useForm<ISiteFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(SiteModelHelper.formSchema()),
        mode: 'all',
    });

    const currentFormValues = watch();

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const saveNewCoordinates = (coords : LatLng) : void => {
        setValue('latitude', coords.lat, { shouldValidate: true, shouldDirty: true });
        setValue('longitude', coords.lng, { shouldValidate: true, shouldDirty: true });
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const organizationOptions = useMemo(() => {
        return props.organizations
            .filter(x => x.isActive).
            map(OptionType.fromDataModel);
    }, [
        props.organizations
    ]);

    const inspectionPointOptions = useMemo(() => {
        return props.inspectionPoints
            .filter(x => x.isActive)
            .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id }));
    }, [
        props.inspectionPoints
    ]);

    const contactInfoOptions = useMemo(() => {
        return props.contactInfos
            .filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [
        props.contactInfos
    ]);

    const ftpDetailOptions = useMemo(() => {
        return props.ftpDetails
            .filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [
        props.ftpDetails
    ]);

    const currentMapLocation = useMemo(() => {
        // const lat = (props.initialValues.latitude && props.initialValues.latitude !== '-')
        //     ? Number(props.initialValues.latitude)
        //     : defaultCenterPosition.lat;
        // const long = (props.initialValues.longitude && props.initialValues.longitude !== '-')
        //     ? Number(props.initialValues.longitude)
        //     : defaultCenterPosition.lng;
        const currentPosition = new LatLng(props.initialValues.latitude, props.initialValues.longitude);

        return currentPosition;
    }, [currentFormValues]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form className={'p20'} onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdr pb10'}>
                    <div className={'fdc flx1 mr20'}>
                        <Typography variant={'h6'} className={'cp mb10'}>Site Detail</Typography>
                        <div className={'fdr flx1 wfill mb10'}>
                            <ControlledTextField
                                className={'flx1 mr10'}
                                name={'code'}
                                label={'Code'}
                                control={control}
                                defaultValue={props.initialValues.code}
                                size={'small'}
                            />
                            <ControlledTextField
                                className={'flx1'}
                                name={'shortDescription'}
                                label={'Short Description'}
                                control={control}
                                defaultValue={props.initialValues.shortDescription}
                                size={'small'}
                            />
                        </div>
                        <div className={'fdr flx1 wfill mb10'}>
                            <ControlledTextField
                                className={'flx1'}
                                name={'description'}
                                label={'Description'}
                                control={control}
                                defaultValue={props.initialValues.description}
                                size={'small'}
                            />
                        </div>
                        <div className={'fdr flx1 wfill mb10'}>
                            <ControlledTextField
                                className={'flx1 mr10'}
                                name={'exporterName'}
                                label={'Exporter Name'}
                                control={control}
                                defaultValue={props.initialValues.exporterName}
                                size={'small'}
                            />
                            <ControlledTextField
                                className={'flx1'}
                                name={'exporterEmail'}
                                label={'Exporter Email'}
                                control={control}
                                defaultValue={props.initialValues.exporterEmail}
                                size={'small'}
                            />
                        </div>
                        <div className={'fdr flx1 wfill mb10'}>
                            <ControlledTextField
                                className={'flx1 mr10'}
                                name={'globalGapNumber'}
                                label={'Global Gap Number'}
                                control={control}
                                defaultValue={props.initialValues.globalGapNumber}
                                size={'small'}
                            />
                            <ControlledTextField
                                className={'flx1'}
                                name={'telNumber'}
                                label={'Telephone Number'}
                                control={control}
                                defaultValue={props.initialValues.telNumber}
                                size={'small'}
                            />
                        </div>
                        <div className={'fdr flx1 wfill mb10'}>
                            <ControlledTextField
                                className={'flx1'}
                                multiline
                                rows={5}
                                name={'address'}
                                label={'Address'}
                                control={control}
                                defaultValue={props.initialValues.address}
                                size={'small'}
                            />
                        </div>
                        <div className={'fdr flx1 wfill mb10 aic'}>
                            <ControlledTextField
                                className={'flx1 mr10'}
                                name={'faxNumber'}
                                label={'Fax Number'}
                                control={control}
                                defaultValue={props.initialValues.faxNumber}
                                size={'small'}
                            />
                            <ControlledTextField
                                className={'flx1 mr10'}
                                name={'packhouseDateCode'}
                                label={'Packhouse Date Code'}
                                control={control}
                                defaultValue={props.initialValues.packhouseDateCode}
                                size={'small'}
                            />
                            <ControlledMuiAutoCompleteSelect
                                className={'flx1'}
                                name={'inspectionPoint'}
                                label={'Inspection Point'}
                                control={control}
                                options={inspectionPointOptions}
                                defaultValues={props.initialValues.inspectionPoint ?? null}
                            />
                        </div>
                        <div className={'fdr flx1 wfill mb10'}>
                            <ControlledMuiAutoCompleteSelect
                                className={'flx1 mr10'}
                                name={'organizations'}
                                label={'Organizations'}
                                control={control}
                                options={organizationOptions}
                                defaultValues={props.initialValues.organizations}
                                isMulti
                            />
                            <ControlledMuiAutoCompleteSelect
                                className={'flx1 mr10'}
                                name={'contactInfos'}
                                label={'Contact Info'}
                                control={control}
                                options={contactInfoOptions}
                                defaultValues={props.initialValues.contactInfos}
                                isMulti
                            />
                            <ControlledMuiAutoCompleteSelect
                                className={'flx1'}
                                name={'ftpDetails'}
                                label={'FTP Details'}
                                control={control}
                                options={ftpDetailOptions}
                                defaultValues={props.initialValues.ftpDetails}
                                isMulti
                            />
                        </div>
                        <div className={'fdr flx1 mb10'}>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'isPackhouse'}
                                    label={'Is Packhouse'}
                                    control={control}
                                    defaultValue={props.initialValues.isPackhouse}
                                />
                            </div>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'isBRCCompliant'}
                                    label={'Is BRC Compliant'}
                                    control={control}
                                    defaultValue={props.initialValues.isBRCCompliant}
                                />
                            </div>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'isSimple'}
                                    label={'Is Simple'}
                                    control={control}
                                    defaultValue={props.initialValues.isSimple}
                                />
                            </div>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'isDeliveryPoint'}
                                    label={'Is Delivery Point'}
                                    control={control}
                                    defaultValue={props.initialValues.isDeliveryPoint}
                                />
                            </div>
                        </div>
                        <div className={'fdr flx1'}>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'isExternal'}
                                    label={'Is External'}
                                    control={control}
                                    defaultValue={props.initialValues.isExternal}
                                />
                            </div>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'handlesExport'}
                                    label={'Handles Export'}
                                    control={control}
                                    defaultValue={props.initialValues.handlesExport}
                                />
                            </div>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'canMixSalesPoint'}
                                    label={'Can Mix Sales Point'}
                                    control={control}
                                    defaultValue={props.initialValues.canMixSalesPoint}
                                />
                            </div>
                            <div className={'flx1'}>
                                <ControlledLabelledCheckBox
                                    name={'isActive'}
                                    label={'Is Active?'}
                                    control={control}
                                    defaultValue={props.initialValues.isActive}
                                    disabled={props.initialValues.isActive}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={'fdc flx1'}>
                        <Typography variant={'h6'} className={'cp mb10'}>Dispatch Header Logo</Typography>
                        <ControlledImageSelector
                            className={'mb10 wfill h300'}
                            name={'dispatchHeaderLogoImageBase64'}
                            control={control}
                            defaultValue={props.initialValues.dispatchHeaderLogoImageBase64}
                        />
                        <Typography variant={'h6'} className={'cp mb10'}>Location</Typography>
                        <div className={'fdc flx2'}>
                            <div className={'fdr wfill jcsb mb15'}>
                                <ControlledTextField
                                    label={'Latitude'}
                                    name={'latitude'}
                                    className={'wfill mr10'}
                                    control={control}
                                    size={'small'}
                                />
                                <ControlledTextField
                                    label={'Longitude'}
                                    name={'longitude'}
                                    className={'wfill'}
                                    control={control}
                                    size={'small'}
                                />
                            </div>
                            <div className={'h250 wfill pl20 pr20'}>
                                <LocationPicker
                                    centerPosition={currentMapLocation}
                                    markerTitle={
                                        currentFormValues.description.trim() !== ''
                                            ? currentFormValues.description
                                            : 'Site Location'
                                    }
                                    setNewCoordinates={saveNewCoordinates}
                                    layerPosition={'topright'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={'fdr flx1 aic jcfe'}>
                    <Button
                        className={'cpd mr20'}
                        type={'reset'}
                        onClick={props.onClose}
                    >
                        CANCEL
                    </Button>
                    <PillButton
                        className={'pl30 pr30'}
                        text={'SAVE'}
                        size={'small'}
                        type={'submit'}
                        color={'secondary'}
                        disabled={!isDirty || !isValid || props.isLoading}
                    />
                </div>
            </form>
        </div>
    );
};

export default SiteForm;
